/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo, useEffect } from 'react';
import MyBrandsContext from './MyBrandsContext';
import initialMyBrandsState from './initialMyBrandsState';
import userReducer from 'contexts/myBrands/reducer/myBrands.reducer';
import * as action from 'contexts/myBrands/reducer/myBrands.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { TCreateNewBrandProps } from 'interfaces/myBrands.interface';
import useContextUser from 'hooks/contexts/useContextUser';
import { ChildrenProps } from 'interfaces/general.interface';
import useContextGeneral from 'hooks/contexts/useContextGeneral';
import useContextCompany from 'hooks/contexts/useContextCompany';
import { IgetBrandsAPIResponseDataItem } from 'api/endpoints/myBrands/myBrands.response.interface';

function MyBrandsProvider(props: ChildrenProps) {
  const [myBrandsState, dispatch] = useReducer(userReducer, initialMyBrandsState);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { t: translate } = useTranslation();
  const { dbUser } = useContextUser();
  const { company } = useContextCompany();
  const { associationStates } = useContextGeneral();

  useEffect(() => {
    (async () => {
      if (!company?.id || !isAuthenticated || !associationStates) {
        return false;
      }
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getMyBrandsAction({
        dispatch,
        clientId: company.id,
        token,
        translate,
        associationStates,
      });
    })();
  }, [company?.id, associationStates, isAuthenticated]);

  const getMyBrands = useCallback(async () => {
    if (!company?.id || !associationStates) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.getMyBrandsAction({
      dispatch,
      clientId: company.id,
      token,
      translate,
      associationStates,
    });
  }, [company?.id, associationStates]);

  const addToMyBrand = useCallback(
    async ({ brandId, tenantId }: { brandId: string; tenantId: string }) => {
      if (!company?.id || !associationStates || !company?.country?.id) {
        return Promise.resolve({ ok: false, haveCatalog: false });
      }
      const token = await getAccessTokenSilently();
      if (!token) {
        return Promise.resolve({ ok: false, haveCatalog: false });
      }
      return await action.addToMyBrandAction({
        dispatch,
        brandId,
        tenantId,
        clientId: company.id,
        token,
        translate,
        associationStates,
        countryId: company.country.id,
      });
    },
    [company?.id, company?.country?.id, associationStates]
  );

  const addToMyBrandMultiple = useCallback(
    async ({ brands }: { brands: IgetBrandsAPIResponseDataItem[] }) => {
      if (!company?.id || !associationStates || !company?.country?.id) return;
      const token = await getAccessTokenSilently();
      if (!token) return;
      return await action.addToMyBrandMultipleAction({
        dispatch,
        myBrands: myBrandsState.myBrands,
        brands,
        clientId: company.id,
        token,
        translate,
        associationStates,
        countryId: company.country.id,
      });
    },
    [company?.id, company?.country?.id, associationStates, myBrandsState.myBrands]
  );

  const removeFromMyBrand = useCallback(
    async ({ associationId }: { associationId: string }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.removeFromMyBrandAction({
        dispatch,
        associationId,
        token,
        translate,
      });
    },
    []
  );

  const removeFromMyBrandsMultiple = useCallback(
    async ({ associationsIds }: { associationsIds: string[] }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.removeFromMyBrandsMultipleAction({
        dispatch,
        associationsIds,
        myBrands: myBrandsState.myBrands,
        token,
        translate,
      });
    },
    [myBrandsState.myBrands]
  );

  const createNewBrand = useCallback(
    async ({ brand }: { brand: TCreateNewBrandProps }) => {
      if (!company?.id || !associationStates || !dbUser?.name) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createNewBrandAction({
        dispatch,
        brand,
        clientId: company.id,
        dbUserName: dbUser.name,
        token,
        translate,
        associationStates,
      });
    },
    [company?.id, associationStates, dbUser?.name]
  );

  const resendAssociationEmail = useCallback(
    async ({
      tenantId,
      to,
    }: {
      tenantId: string;
      to?: { email: string; name: string };
    }) => {
      if (!company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.resendAssociationEmailAction({
        dispatch,
        tenantId,
        to,
        clientId: company.id,
        token,
        translate,
      });
    },
    [company?.id]
  );

  const memoProvider = useMemo(
    () => ({
      ...myBrandsState,
      getMyBrands,
      removeFromMyBrand,
      removeFromMyBrandsMultiple,
      addToMyBrand,
      createNewBrand,
      addToMyBrandMultiple,
      resendAssociationEmail,
    }),
    [
      myBrandsState,
      getMyBrands,
      removeFromMyBrand,
      removeFromMyBrandsMultiple,
      addToMyBrand,
      createNewBrand,
      addToMyBrandMultiple,
      resendAssociationEmail,
    ]
  );

  return (
    <MyBrandsContext.Provider value={memoProvider}>
      {props.children}
    </MyBrandsContext.Provider>
  );
}

export default memo(MyBrandsProvider);
